import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import type { PaginationState } from '@tanstack/react-table';

import { DEFAULT_PAGINATED_LIST_DTO } from '@apple/utils/pagination/models';
import type { Guid } from '@apple/utils/primitive';

import { getStoreEventAudits } from '../api/audit';
import { getAllCategories, getStoreEventCategorySelections } from '../api/categories';
import { getStoreEventHeader } from '../api/heading';
import { getLineFulfillmentInfo, getLinesByCategory } from '../api/lines';
import { getStoreEvent, getStoreIdsForExistingEvents } from '../api/management';
import { getShipmentReport } from '../api/shipment-report';
import type { CategoryLinesPaginatedList, StoreEventLineFilters } from '../models/lines';
import type { StoreEventFilters } from '../models/listing';

export const storeEventQueryKeys = {
	all: ['store-events'],
	categories: () => [...storeEventQueryKeys.all, 'categories'],
	list: () => [...storeEventQueryKeys.all, 'list'],
	listPage: (pagination: PaginationState, filters: StoreEventFilters) => [
		...storeEventQueryKeys.list(),
		pagination,
		filters,
	],
	getStoreIdsForExistingEvents: () => [...storeEventQueryKeys.all, 'existing-store-ids'],
	storeEvent: (storeEventId: Guid) => [...storeEventQueryKeys.all, storeEventId],
	header: (storeEventId: Guid) => [...storeEventQueryKeys.storeEvent(storeEventId), 'header'],
	audit: (storeEventId: Guid) => [...storeEventQueryKeys.storeEvent(storeEventId), 'audit'],
	selectedCategories: (storeEventId: Guid) => [
		...storeEventQueryKeys.all,
		storeEventId,
		'categories',
	],
	lines: (storeEventId: Guid) => [...storeEventQueryKeys.storeEvent(storeEventId), 'lines'],
	linesByCategory: (storeEventId: Guid, filter: StoreEventLineFilters) => [
		...storeEventQueryKeys.lines(storeEventId),
		filter,
	],
	lineFulfillmentInfo: (storeEventId: Guid, storeEventCategoryIds: Guid[]) => [
		...storeEventQueryKeys.storeEvent(storeEventId),
		'fulfillment-info',
		storeEventCategoryIds,
	],
	shipmentReport: (storeEventId: Guid) => [
		...storeEventQueryKeys.storeEvent(storeEventId),
		'shipment-report',
	],
};

export const storeEventQueries = {
	categories: queryOptions({
		queryKey: storeEventQueryKeys.categories(),
		queryFn: ({ signal }) => getAllCategories(signal),
		placeholderData: keepPreviousData,
		initialDataUpdatedAt: 0,
		initialData: [],
	}),
	audit: (storeEventId: Guid) =>
		queryOptions({
			queryKey: storeEventQueryKeys.audit(storeEventId),
			queryFn: ({ signal }) => getStoreEventAudits(storeEventId, signal),
			placeholderData: keepPreviousData,
			staleTime: 5 * 60 * 1000, // 5 minutes
			initialDataUpdatedAt: 0,
			initialData: [],
		}),
	getById: (storeEventId: Guid) =>
		queryOptions({
			queryKey: storeEventQueryKeys.storeEvent(storeEventId),
			queryFn: ({ signal }) => getStoreEvent(storeEventId, signal),
			placeholderData: keepPreviousData,
			throwOnError: true,
		}),
	header: (storeEventId: Guid) =>
		queryOptions({
			queryKey: storeEventQueryKeys.header(storeEventId),
			queryFn: ({ signal }) => getStoreEventHeader(storeEventId, signal),
			placeholderData: keepPreviousData,
			throwOnError: true,
		}),
	getStoreIdsForExistingEvents: () =>
		queryOptions({
			queryKey: storeEventQueryKeys.getStoreIdsForExistingEvents(),
			queryFn: ({ signal }) => getStoreIdsForExistingEvents(signal),
			placeholderData: keepPreviousData,
			throwOnError: true,
		}),
	selectedCategories: (storeEventId: Guid) =>
		queryOptions({
			queryKey: storeEventQueryKeys.selectedCategories(storeEventId),
			queryFn: ({ signal }) => getStoreEventCategorySelections(storeEventId, signal),
			placeholderData: keepPreviousData,
		}),
	linesByCategory: (filter: StoreEventLineFilters) =>
		queryOptions({
			queryKey: storeEventQueryKeys.linesByCategory(filter.storeEventId, filter),
			queryFn: ({ signal }) => getLinesByCategory(filter.storeEventId, filter, signal),
			placeholderData: keepPreviousData,
			initialDataUpdatedAt: 0,
			initialData: {
				...DEFAULT_PAGINATED_LIST_DTO,
				categoryCount: 0,
				nonZeroQuantityItems: 0,
			} as CategoryLinesPaginatedList,
		}),
	lineFulfillmentInfo: (storeEventId: Guid, storeEventCategoryIds: Guid[], enabled = true) =>
		queryOptions({
			queryKey: storeEventQueryKeys.lineFulfillmentInfo(storeEventId, storeEventCategoryIds),
			queryFn: ({ signal }) =>
				getLineFulfillmentInfo(storeEventId, storeEventCategoryIds, signal),
			enabled,
		}),
	shipmentReport: (storeEventId: Guid) =>
		queryOptions({
			queryKey: storeEventQueryKeys.shipmentReport(storeEventId),
			queryFn: ({ signal }) => getShipmentReport(storeEventId, signal),
			throwOnError: true,
		}),
};
